import React, { useState, useCallback, useMemo, useReducer } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  Radio,
  RadioGroup,
} from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import FormActionBar from '../../../shared/components/FormActionBar';
import TagEditor from '../../../shared/components/TagEditor';
import { useAuth } from '../../auth/context/AuthContext';
import { useEventManagementService } from '../services/eventManagementService';
import RuleScheduleSection from './RuleScheduleSection';
import RuleTargetSection from './RuleTargetSection';
import type { RuleTemplate, TemplateOwnerType } from '../../../types/frontend';
import type { OpsItemStrategyType, RuleTypeType, NotificationTarget } from '../../../types/backend';
import { OpsItemStrategy, RuleType } from '../../../types/backend';
import {
  formReducer,
  buildInitialForm,
  validateRuleForm,
  templateItsmConfigToChannel,
  templateNotificationsToTargets,
  templateAutomationPromptToChannel,
} from '../utils/ruleFormUtils';
import type { RuleFormState } from '../utils/ruleFormUtils';
import {
  DEFAULT_SCHEDULE_HOUR,
  DEFAULT_SCHEDULE_MINUTE,
  parseCronTime,
} from '../utils/scheduleUtils';

const CATEGORIES = ['Security', 'Cost', 'Compute', 'Storage', 'Governance', 'Monitoring', 'Database', 'Custom'] as const;

interface TemplateEditorProps {
  customerId: string;
  customerName?: string;
  template: RuleTemplate | null;
  isNew: boolean;
  suggestedTags?: string[];
  onSaved: (savedTemplate: RuleTemplate) => void;
  onDelete: () => void;
  onDiscard: () => void;
  onUseAsRule: () => void;
}

function templateToFormInit(template: RuleTemplate | null): RuleFormState {
  if (!template) {
    return buildInitialForm(null, null, null);
  }
  const parsedTime = template.schedule_expression ? parseCronTime(template.schedule_expression) : null;
  return {
    ...buildInitialForm(null, null, null),
    ruleType: (template.rule_type || 'alert') as RuleTypeType,
    description: template.description || '',
    triggerType: (template.trigger_type || 'event') as 'event' | 'schedule',
    eventPattern: template.event_pattern || {},
    scheduleHour: parsedTime?.hour ?? DEFAULT_SCHEDULE_HOUR,
    scheduleMinute: parsedTime?.minute ?? DEFAULT_SCHEDULE_MINUTE,
    enrichmentPromptId: template.enrichment_prompt_id ?? null,
    itsm: templateItsmConfigToChannel(template.itsm_config),
    notificationTargets: templateNotificationsToTargets(template.notifications),
    automation: templateAutomationPromptToChannel(template.automation_prompt_id, template.automation_agent_id),
    opsitemStrategy: (template.opsitem_strategy || OpsItemStrategy.TRACK) as OpsItemStrategyType,
  };
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  customerId,
  customerName,
  template,
  isNew,
  suggestedTags = [],
  onSaved,
  onDelete,
  onDiscard,
  onUseAsRule,
}) => {
  const eventService = useEventManagementService();
  const { isAdmin } = useAuth();
  // Mirrors the Cedar role gate on Create/Update/DeleteGlobalTemplate: the opspilot-admin or
  // msreportingadmin role, never a level (isAdmin is false under View As).
  const canWriteGlobalTemplates = isAdmin;

  const [form, dispatch] = useReducer(formReducer, null, () => templateToFormInit(template));

  const [templateName, setTemplateName] = useState(template?.name || '');
  const [templateDescription, setTemplateDescription] = useState(template?.description || '');
  const [category, setCategory] = useState(template?.category || 'Custom');
  const [tags, setTags] = useState<string[]>(template?.tags || []);
  const [newTemplateOwner, setNewTemplateOwner] = useState<TemplateOwnerType>('customer');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (!templateName.trim()) errors.templateName = 'Name is required';
    if (!category) errors.category = 'Category is required';
    const formErrors = validateRuleForm(form);
    if (formErrors.eventPattern) errors.eventPattern = formErrors.eventPattern;
    // Fail loud on an unsupported webhook target (matches RuleEditor) rather than
    // silently dropping it on save — webhook delivery has no backend yet.
    if (formErrors.webhook_unavailable) errors.webhook_unavailable = formErrors.webhook_unavailable;
    return errors;
  }, [templateName, category, form]);

  const isFormValid = Object.keys(validationErrors).length === 0;

  const setField = useCallback(<K extends keyof RuleFormState>(field: K, value: RuleFormState[K]) => {
    dispatch({ type: 'SET_FIELD', field, value });
  }, []);

  const handleSave = useCallback(async () => {
    setHasAttemptedSave(true);
    if (Object.keys(validationErrors).length > 0) {
      setError('Please fix validation errors');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const data: Record<string, unknown> = {
        name: templateName.trim(),
        description: templateDescription.trim(),
        category,
        tags,
        rule_type: form.ruleType,
        trigger_type: form.triggerType,
        event_pattern: form.triggerType === 'event' ? form.eventPattern : undefined,
        schedule_expression: form.triggerType === 'schedule' ? form.cronExpression || undefined : undefined,
        enrichment_prompt_id: form.enrichmentPromptId || undefined,
        itsm_config: form.itsm || undefined,
        // Validation blocks save when a webhook target is present, so this filter
        // narrows to the backend's email|sns NotificationTarget without dropping
        // any user data at runtime.
        notifications: form.notificationTargets.filter(
          (t): t is NotificationTarget => t.channel !== 'webhook',
        ),
        automation_prompt_id: form.automation?.prompt_id || undefined,
        automation_agent_id: form.automation?.prompt_id ? form.automation.agent_id : undefined,
        opsitem_strategy: form.opsitemStrategy,
      };

      const saved = template && !isNew
        ? await eventService.updateTemplate(template.template_id, data, template.owner, customerId)
        : await eventService.createTemplate(data, newTemplateOwner, customerId);

      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  }, [customerId, form, template, isNew, newTemplateOwner, templateName, templateDescription, category, tags, validationErrors, eventService, onSaved]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {isNew ? 'Create Template' : 'Edit Template'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {template && !isNew && (
          <Button variant="outlined" size="small" onClick={onUseAsRule}>
            Use as Rule
          </Button>
        )}
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
        {error && (
          <InlineAlert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </InlineAlert>
        )}

        {isNew && canWriteGlobalTemplates && (
          <FormControl sx={{ mb: 3, maxWidth: 800, display: 'block' }}>
            <FormLabel id="template-owner-label" sx={{ typography: 'caption', color: 'text.secondary' }}>
              Available to
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="template-owner-label"
              value={newTemplateOwner}
              onChange={(e) => setNewTemplateOwner(e.target.value as TemplateOwnerType)}
            >
              <FormControlLabel
                value="customer"
                control={<Radio size="small" />}
                label={<Typography variant="body2">{customerName ? `This customer only: ${customerName}` : 'This customer only'}</Typography>}
              />
              <FormControlLabel
                value="global"
                control={<Radio size="small" />}
                label={<Typography variant="body2">All customers (shared)</Typography>}
              />
            </RadioGroup>
            <FormHelperText sx={{ mx: 0 }}>
              Shared templates appear for every operator on every customer. Only admins can create them.
            </FormHelperText>
          </FormControl>
        )}

        <Box sx={{ mb: 3, maxWidth: 800 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Tags
          </Typography>
          <TagEditor value={tags} onChange={setTags} suggestions={suggestedTags} />
        </Box>

        {/* Template Info */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3, maxWidth: 800 }}>
          <TextField
            fullWidth
            required
            size="small"
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            error={hasAttemptedSave && !!validationErrors.templateName}
            helperText={hasAttemptedSave && validationErrors.templateName}
            placeholder="e.g. Security Alert"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" fullWidth error={hasAttemptedSave && !!validationErrors.category}>
            <InputLabel shrink>Category</InputLabel>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              label="Category"
              notched
            >
              {CATEGORIES.map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel shrink>Rule Type</InputLabel>
            <Select
              value={form.ruleType}
              onChange={(e) => setField('ruleType', e.target.value as RuleTypeType)}
              label="Rule Type"
              notched
            >
              <MenuItem value={RuleType.ALERT}>Alert</MenuItem>
              <MenuItem value={RuleType.AUTOMATION}>Automation</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Description"
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            placeholder="What does this template do?"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel shrink>Action log item strategy</InputLabel>
            <Select
              value={form.opsitemStrategy}
              onChange={(e) => setField('opsitemStrategy', e.target.value as OpsItemStrategyType)}
              label="Action log item strategy"
              notched
            >
              <MenuItem value={OpsItemStrategy.TRACK}>Track — one rolling action log item per rule</MenuItem>
              <MenuItem value={OpsItemStrategy.ALWAYS_NEW}>Always new — fresh action log item every fire</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Track dedups repeated firings onto one action log item; Always new opens a new one each time.
            </Typography>
          </FormControl>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Two-panel: Trigger | Actions — same as RuleEditor */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3, alignItems: 'start' }}>
          {/* Left: Trigger */}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Trigger
            </Typography>
            <RuleScheduleSection
              form={form}
              setField={setField}
              hasAttemptedSave={hasAttemptedSave}
              validationErrors={validationErrors}
            />
          </Box>

          {/* Right: Actions */}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Actions
            </Typography>

            <RuleTargetSection
              form={form}
              dispatch={dispatch}
              setField={setField}
              hasAttemptedSave={hasAttemptedSave}
              validationErrors={validationErrors}
              saving={saving}
              customerId={customerId}
            />
          </Box>
        </Box>
      </Box>

      {/* Action Bar */}
      <FormActionBar
        onSave={handleSave}
        saving={saving}
        saveDisabled={!isFormValid}
        saveLabel={isNew ? 'Create Template' : 'Save Template'}
        size="small"
        onDelete={template && !isNew ? onDelete : undefined}
        deleteLabel="Delete"
        sx={{ px: 3, py: 1.5, mt: 0, pt: 0 }}
      >
        <Button
          variant="outlined"
          onClick={onDiscard}
          disabled={saving}
          size="small"
        >
          Discard
        </Button>
      </FormActionBar>
    </Box>
  );
};

export default TemplateEditor;
